import { computed, type ComputedRef, type Ref } from 'vue'
import { isDayjs } from 'dayjs'
import type { TQDataTablePagination } from '../qTypes'
import type { PDataTableColumn } from '../types'

export const useClientSort = <T>(
  items: Ref<T[]>,
  columns: Ref<PDataTableColumn[]>,
  qPagination: Readonly<Ref<TQDataTablePagination>>,
): ComputedRef<T[]> => {
  const getColumnValue = (col: PDataTableColumn, row: T): T[keyof T] | undefined => {
    if (!col) {
      return undefined
    }

    if (typeof col.field === 'function') {
      try {
        return col.field(row)
      } catch {
        return undefined
      }
    }

    if (typeof col.field === 'string' && (row as any)[col.field] !== undefined) {
      return (row as any)[col.field]
    }

    if (typeof col.name === 'string' && (row as any)[col.name] !== undefined) {
      return (row as any)[col.name]
    }

    return undefined
  }

  const compareValues = (aVal: any, bVal: any): number => {
    if (aVal === bVal) {
      return 0
    }

    if (aVal == null) {
      return -1
    }

    if (bVal == null) {
      return 1
    }

    if (typeof aVal === 'boolean' || typeof bVal === 'boolean') {
      const A = aVal ? 1 : 0
      const B = bVal ? 1 : 0

      return A - B
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return aVal - bVal
    }

    if (isDayjs(aVal) && isDayjs(bVal)) {
      return aVal.valueOf() - bVal.valueOf()
    }

    const aStr = String(aVal)
    const bStr = String(bVal)

    return aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' })
  }

  return computed<T[]>(() => {
    let arr = [...items.value]

    if (!qPagination.value.sortBy) {
      return arr
    }

    const { sortBy, descending } = qPagination.value
    const column = columns.value.find((c: PDataTableColumn) => c.name === sortBy)

    if (column) {
      arr.sort((a, b) => {
        const aVal = getColumnValue(column, a)
        const bVal = getColumnValue(column, b)

        let comparison: number

        if (typeof column.sort === 'function') {
          try {
            const result = column.sort(aVal, bVal, a, b)
            if (typeof result === 'number') {
              comparison = result
            } else if (typeof result === 'boolean') {
              comparison = result ? 1 : -1
            } else {
              comparison = compareValues(aVal, bVal)
            }
          } catch {
            comparison = compareValues(aVal, bVal)
          }
        } else {
          comparison = compareValues(aVal, bVal)
        }

        return descending ? -comparison : comparison
      })
    }

    if (qPagination.value.rowsPerPage && qPagination.value.rowsPerPage > 0) {
      const start = (qPagination.value.page - 1) * qPagination.value.rowsPerPage

      arr = arr.slice(start, start + qPagination.value.rowsPerPage)
    }

    return arr
  })
}
